import React, { useState, ChangeEvent, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Stack,
  Pagination,
  TextField,
  InputAdornment,
  Tooltip,
  TableHead,
  Typography
} from '@mui/material';
import styles from './orders-list.module.scss';
import ShareIcon from '@mui/icons-material/Share';
import { getOrders, htmlId } from './orders-list.service';
import { IOrderDto } from '../../../components/order/interfaces/order-dto.interface';
import { IOrderListViewDto } from '../../../components/order/interfaces/order-list-view-dto.interface';
import moment from 'moment';
import OrdersListSkeleton from './orders-list-skeleton';
import { useTranslation } from '../../../i18n/useTranslation';
import ButtonGroupList, { ButtonItem } from '../../../shared/button-group-list/button-group-list';
import { DocumentFileContent } from '../../../shared/models/document-file-content.model';
import { getExcelOrderSummary, getPdfOrderSummary, getTotalQuantityShoppingCart, reOpenOrderInShoppingCart } from '../../../components/shopping-cart/services/shopping-cart.service';
import { getAndShowFileDocument } from '../../../shared/services/attachments.service';
import { toast } from 'react-toastify';
import { pdfFileNameOrderSummary } from '../../shopping-cart-container/order-confirmation/order-confirmation.service';
import { FileDocumentTypeCode } from '../../../shared/models/file-document-type.enum';
import ErrorAlert from '../../../shared/alerts/error-alert';
import OrderDetailsDialog from '../order-details.dialog';
import { claimIsActive } from '../../../shared/services/claims.service';
import { ErpClaimCode } from '../../../shared/models/erp-claim-code.enum';
import { PermissionsUseBetween } from '../../../shared/use-between/permissions-use-between';
import { useBetween } from 'use-between';
import { IShopingCart } from '../../../components/order/interfaces/shopping-cart.interface';
import { ShoppingCartUseBetween } from '../../../components/order/use-between/shopping-cart-use-between';
import { useNavigate } from 'react-router-dom';
import { SHOPPING_CART_PATHS } from '../../../shared/constants/breadcrumb.constants';
import { getCommonErrorTranslate } from '../../../configuration/axios-configuration/axios.erros.interceptor.service';

const OrdersList: React.FC = () => {
  const { t } = useTranslation('translation');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(0);
  const [orders, setOrders] = useState<IOrderListViewDto[]>([]);
  const [isSkeletonLoader, setIsSkeletonLoader] = useState<boolean>(true);
  const [orderDetailsDialogIsOpen, setOpenOrderDetailsDialog] = useState(false);
  const [documentId, setDocumentId] = useState<string>();
  const [documentNumber, setDocumentNumber] = useState<string>();
  const [documentDate, setDocumentDate] = useState<Date>();
  const [documentStatusName, setDocumentStatusName] = useState<string | null>();
  const [orderBondFileUuid, setOrderBondFileUuid] = useState<string | null>();
  const { claims } = useBetween(PermissionsUseBetween);
  const [permissionAlertIsOpen, setOpenPermissionAlert] = useState(false);
  const { setShoppingCart } = useBetween(ShoppingCartUseBetween);
  const { setBuildingSite } = useBetween(ShoppingCartUseBetween);
  const { setIsReadyToOrder } = useBetween(ShoppingCartUseBetween);
  const { setQuantityShoppingCart } = useBetween(ShoppingCartUseBetween);
  const navigate = useNavigate();
  const showError = (message: string) => toast.error(message);

  const searchActionButtons: ButtonItem[] = [
      {id: `${htmlId}-search-btn`, label: t('SEARCH')},
      {id: `${htmlId}-clear-search-btn`, label: t('CLEAR_SEARCH')}
  ];

  const rowActionButtons: ButtonItem[] = [
      {id: `${htmlId}-export-to-pdf-btn`, label: 'Pdf'},
      {id: `${htmlId}-export-to-excel-btn`, label: t('Excel')},
      {id: `${htmlId}-details-btn`, label: t('DETAILS')},
      {id: `${htmlId}-re-order-btn`, label: t('RE_ORDER')}
  ];

  useEffect(() => {
      fetchOrders(page);
  }, [page]);

  const fetchOrders = (page: number, isClearSerach: boolean = false) => {
    setIsSkeletonLoader(true);
    getOrders(getDto(page, isClearSerach)).then((response) => {
      if (response) {
        const dto: IOrderDto = response.data;
        setOrders(dto.data);
        setPage(dto.page);
        setCount(dto.count);
        setPageSize(dto.pageSize);
        setIsSkeletonLoader(false);
      }
    })
  }

  const getDto = (page: number, isClearSerach: boolean = false) => {
    return {
        data: [],
        count: count,
        page: page,
        searchInput: !isClearSerach ? search : '',
        pageSize: pageSize,
        fetchPerUserOnly: false
    } as IOrderDto;
  }

  const handleSearchActionButtonClick = (id: string) => {
      switch (id) {
          case `${htmlId}-search-btn`:
            fetchOrders(1);
          break;
          case `${htmlId}-clear-search-btn`:
            setSearch('');
            fetchOrders(1, true);
          break;
      }
  }

  const handleRowActionButtonClick = (id: string, item: IOrderListViewDto) => {
      switch (id) {
          case `${htmlId}-export-to-pdf-btn`:
            handleGetPdfOrderSummary(item.id, item.documentNumber, item.documentDate, item.documentStatusName);
          break;
          case `${htmlId}-export-to-excel-btn`:
            handleGetExcelOrderSummary(item.id, item.documentNumber, item.documentDate, item.documentStatusName);
          break;
          case `${htmlId}-details-btn`:
            openOrderDetailsDialog(item);
          break;
          case `${htmlId}-re-order-btn`:
            handleReOpenOrderInShoppingCart(item.id);
          break;
      }
  }

  const handleGetPdfOrderSummary = (documentId: string, documentNumber: string, documentDate: Date, documentStatusName: string | null) => {
      setIsSkeletonLoader(true);
      getPdfOrderSummary(documentId, documentNumber, documentDate, documentStatusName ? documentStatusName : '-').then((response) => {
          if (response) {
              const model: DocumentFileContent = response.data;
              if (!model.message) {
                  getAndShowFileDocument(model.file, pdfFileNameOrderSummary, FileDocumentTypeCode.PDF);
                  setIsSkeletonLoader(false);
              } else {
                  showError(getTranslate('MESSAGE_9'));
                  setIsSkeletonLoader(false);
              }
          } else {
              showError(getTranslate('MESSAGE_9'));
              setIsSkeletonLoader(false);
          }
      }).catch(() => {
          showError(getTranslate('MESSAGE_9'));
          setIsSkeletonLoader(false);
      });
  }

  const handleGetExcelOrderSummary = (documentId: string, documentNumber: string, documentDate: Date, documentStatusName: string | null) => {
      setIsSkeletonLoader(true);
      getExcelOrderSummary(documentId, documentNumber, documentDate,  documentStatusName ? documentStatusName : '-').then((response) => {
          if (response) {
              const model: DocumentFileContent = response.data;
              if (!model.message) {
                  getAndShowFileDocument(model.file, documentNumber, FileDocumentTypeCode.EXCEL, false);
                  setIsSkeletonLoader(false);
              } else {
                  showError(getTranslate('MESSAGE_9'));
                  setIsSkeletonLoader(false);
              }
          } else {
              showError(getTranslate('MESSAGE_9'));
              setIsSkeletonLoader(false);
          }
      }).catch(() => {
          showError(getTranslate('MESSAGE_9'));;
          setIsSkeletonLoader(false);
      });
  }

  const openOrderDetailsDialog = (item: IOrderListViewDto) => {
      setDocumentId(item.id);
      setDocumentNumber(item.documentNumber);
      setDocumentDate(item.documentDate);
      setDocumentStatusName(item.documentStatusName);
      setOrderBondFileUuid(item.orderBondFileUuid);
      setOpenOrderDetailsDialog(true)
  }

  const handleReOpenOrderInShoppingCart = (documentId: string) => {
      if (!claimIsActive(claims, ErpClaimCode.CUSTOMER_ECOM_ROLE)) {
          setOpenPermissionAlert(true);
      } else {
          setIsSkeletonLoader(true);
          reOpenOrderInShoppingCart(documentId).then((response) => {
              if (response) {
                  const data: IShopingCart = response.data;
                  setShoppingCart(data);
                  setBuildingSite(data.buildingSite);
                  setIsReadyToOrder(data.isReadyToOrder);
                  setQuantityShoppingCart(getTotalQuantityShoppingCart(data));
                  navigate(SHOPPING_CART_PATHS[1].link);
                  setIsSkeletonLoader(false);
              }
          })
      }
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const getTranslate = (code: string) => {
    return t('FIXCENTER_ORDERING.' + code);
  }

  return (
    <Box
      sx={{
        width: '100%',
        px: '150px',
        py: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box>
        <Typography>{t('ORDERS_ALL')}</Typography>
      </Box>
          <ErrorAlert isOpen={permissionAlertIsOpen} handleCloseDialog = {(val: boolean) => setOpenPermissionAlert(val)} translateCode={getCommonErrorTranslate(ErpClaimCode[ErpClaimCode.CUSTOMER_ECOM_ROLE])}></ErrorAlert>
          <OrderDetailsDialog isOpen={orderDetailsDialogIsOpen} handleCloseDialog = {(isOpen: boolean) => setOpenOrderDetailsDialog(isOpen)} documentId={documentId as string} documentNumber={documentNumber as string} documentDate={documentDate as Date} documentStatusName={documentStatusName as string | null} orderBondFileUuid={orderBondFileUuid as string | null}></OrderDetailsDialog>
      <Paper
        sx={{
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          boxShadow: 'none',
          border: 'none',
          gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, marginBottom: 0, minWidth: '100%' }}>
            <TextField
                label= {t('DOCUMENT_NUMBER')}
                variant="outlined"
                value={search}
                onChange={handleSearchChange}
                size='small'
                InputProps={{
                    endAdornment: (
                    <InputAdornment position="end">
                        <ButtonGroupList buttons={searchActionButtons} size='small' onClick={(id: string) => {handleSearchActionButtonClick(id)}} />
                    </InputAdornment>
                    ),
                }}
                fullWidth
            />
          </Box>
        </Paper>
        {isSkeletonLoader ? <OrdersListSkeleton/> : 
            <Box
            sx={{
              border: '1px solid #ddd',
              borderRadius: 1,
              maxHeight: '50vh',
              transition: 'max-height 0.3s ease',
            }}
            >
             <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table>
                  <TableHead>
                      <TableRow>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('NUMBER')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('DATE')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('STATUS')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('ISSUER')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('CLIENT')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}></TableCell>
                      </TableRow>
                  </TableHead>

                  <TableBody>
                      {orders
                      .map((item, index) => (
                          <TableRow
                              key={item.id}
                              sx={{
                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                              }}
                          >
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {item.documentNumber}
                              {item.isShared ? <ShareIcon style={{'fontSize': "17px", 'marginLeft': "5px"}}></ShareIcon> : ''}
                              {item.isShared ?<Tooltip placement={'right'} title={t(getTranslate('MESSAGE_10'))}><span className={styles.shared_icon_text}>?</span></Tooltip> : ''}
                          </TableCell>
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {(moment(item.documentDate)).format('DD/MM/YYYY')}
                          </TableCell>
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {t(item.documentStatusName)}
                          </TableCell>
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {item.firstContractorName}
                          </TableCell>
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {item.secondContractorName}
                          </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                paddingY: 1,
                                verticalAlign: 'middle',
                                width: '1%',
                                whiteSpace: 'nowrap',
                                borderBottom: '1px solid #e0e0e0',
                              }}
                            >
                              <ButtonGroupList buttons={rowActionButtons} size='small' onClick={(id: string) => {handleRowActionButtonClick(id, item)}} />
                            </TableCell>
                          </TableRow>
                      ))}
                  </TableBody>
                </Table>
            </TableContainer>
          </Box>
      }
      <Stack direction="row" justifyContent="center">
        <Pagination
          count={count}
          page={page}
          onChange={handleChangePage}
          sx={{
            '& .MuiPaginationItem-root': {
              color: 'black',
              borderColor: '#821818 !important',
            },
            '& .Mui-selected': {
              backgroundColor: '#821818 !important',
              color: 'white',
              '&:hover': {
                backgroundColor: '#821818 !important',
              },
            },
          }}
        />
      </Stack>
    </Box>
  );
};
export default OrdersList;